export type UserProfileRole = "USER" | "ADMIN";

export interface OnboardingConfig {
  isOnboardingIncomplete?: boolean;
  hasSeenWelcomeScreen?: boolean;
}

export interface LegalDocumentStatus {
  requiresTermsUpdate: boolean;
  termsAcceptedVersion: string;
  latestSystemVersion: string;
}

export interface UserProfile {
  name: string;
  email: string;
  phone?: string;
  role: UserProfileRole;
  emailVerified: boolean;
  onboardingConfig?: OnboardingConfig;
  legalStatus: {
    privacy_policy: LegalDocumentStatus;
    eula: LegalDocumentStatus;
  };
}

/** Raw API shape for GET /user/me (legacy field names). */
export interface UserProfileApiResponse extends UserProfile {
  phone_number?: string | null;
}

export interface UpdateProfileRequest {
  name?: string;
  phone?: string;
  onboardingConfig?: OnboardingConfig;
}

/** Payload expected by PUT /user/me. */
export interface UpdateProfileApiRequest {
  name?: string;
  phone?: string;
  onboardingConfig?: OnboardingConfig;
}

export function sanitizePhoneNumber(phone?: string | null): string {
  return phone?.trim() ?? "";
}

export function normalizeUserProfileRole(
  role: UserProfileRole | undefined | null,
): UserProfileRole {
  return role === "ADMIN" ? "ADMIN" : "USER";
}

export function normalizeUserProfile(
  profile: UserProfileApiResponse,
): UserProfile {
  const normalizedPhone = sanitizePhoneNumber(
    profile.phone ?? profile.phone_number,
  );

  return {
    name: profile.name,
    email: profile.email,
    role: normalizeUserProfileRole(profile.role),
    emailVerified: profile.emailVerified,
    legalStatus: profile.legalStatus,
    onboardingConfig: profile.onboardingConfig,
    ...(normalizedPhone ? { phone: normalizedPhone } : {}),
  };
}

export function buildUpdateProfilePayload(
  update: UpdateProfileRequest,
  currentProfile?: UserProfile | null,
): UpdateProfileApiRequest {
  const payload: UpdateProfileApiRequest = {
    name: update.name ?? currentProfile?.name,
  };

  if (Object.hasOwn(update, "phone")) {
    const sanitizedPhone = sanitizePhoneNumber(update.phone);
    if (sanitizedPhone) {
      payload.phone = sanitizedPhone;
    }
  }

  if (update.onboardingConfig) {
    payload.onboardingConfig = {
      ...(currentProfile?.onboardingConfig ?? DEFAULT_ONBOARDING_CONFIG),
      ...update.onboardingConfig,
    };
  }

  return payload;
}

/** Removes null/undefined keys and never sends an empty/null phone. */
export function serializeProfileUpdatePayload(
  payload: UpdateProfileApiRequest,
): UpdateProfileApiRequest {
  const { phone, ...rest } = payload;
  const serialized = Object.fromEntries(
    Object.entries(rest).filter(
      ([, value]) => value !== null && value !== undefined,
    ),
  ) as UpdateProfileApiRequest;

  const sanitizedPhone = sanitizePhoneNumber(phone);
  if (sanitizedPhone) {
    serialized.phone = sanitizedPhone;
  }

  return serialized;
}

export interface UpdateProfileResponse {
  message: string;
}

export interface ProfileViewModelState {
  profileData: UserProfile | null;
  isEditing: boolean;
}

/** Fallback when merging partial onboarding updates (PUT /user/me). */
export const DEFAULT_ONBOARDING_CONFIG: Required<OnboardingConfig> = {
  isOnboardingIncomplete: true,
  hasSeenWelcomeScreen: false,
};

/** True when user-level onboarding is finished (profile from GET /user/me). */
export function isUserOnboardingComplete(
  profile: UserProfile | null | undefined,
): boolean {
  return profile?.onboardingConfig?.isOnboardingIncomplete === false;
}

/** True while first-time onboarding is still in progress. */
export function isUserOnboardingInProgress(
  profile: UserProfile | null | undefined,
): boolean {
  return profile?.onboardingConfig?.isOnboardingIncomplete === true;
}
